/**
 * StatusManagement - Status change controls and history
 * Allows users to change order status and view status history
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { unifiedOrderService } from '../../services/unifiedOrderService';
import { UnifiedOrder } from '../../types/unifiedOrderTypes';
import StatusProgress from './StatusProgress';
import { formatDateTime } from '../../utils/dateUtils';

interface StatusManagementProps {
  order: UnifiedOrder;
  onStatusChange: (newStatus: string) => void;
}

interface StatusTransition {
  status: string;
  metadata: {
    label: string;
    color: string;
    icon: string;
    description: string;
  };
}

interface StatusChangeLog {
  id: string;
  order_id: string;
  from_status: string;
  to_status: string;
  changed_by: string;
  change_reason?: string;
  change_notes?: string;
  created_at: string;
}

export default function StatusManagement({ order, onStatusChange }: StatusManagementProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [availableTransitions, setAvailableTransitions] = useState<StatusTransition[]>([]);
  const [statusHistory, setStatusHistory] = useState<StatusChangeLog[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    fetchAvailableTransitions();
  }, [order.id, order.status]);

  const fetchAvailableTransitions = async () => {
    try {
      const response = await unifiedOrderService.getStatusTransitions(order.id);
      if (response.success) {
        setAvailableTransitions(response.data.availableTransitions);
        setCanEdit(response.data.canEdit);
      }
    } catch (err) {
      console.error('Error fetching status transitions:', err);
    }
  };

  const fetchStatusHistory = async () => {
    try {
      setLoading(true);
      const response = await unifiedOrderService.getStatusHistory(order.id);
      if (response.success && response.data) {
        setStatusHistory(response.data);
      }
    } catch (err) {
      console.error('Error fetching status history:', err);
      setError('Fehler beim Laden des Status-Verlaufs'); /* Failed to load status history */
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedStatus) return;

    try {
      setLoading(true);
      setError(null);

      const response = await unifiedOrderService.changeOrderStatus(order.id, {
        to_status: selectedStatus,
        changed_by: 'current_user', // TODO: Get from auth context
        change_reason: changeReason,
        change_notes: changeNotes
      });

      if (response.success) {
        onStatusChange(selectedStatus);
        setDialogOpen(false);
        setSelectedStatus('');
        setChangeReason('');
        setChangeNotes('');
        // Refresh available transitions
        fetchAvailableTransitions();
      } else {
        setError(response.message || 'Fehler beim Ändern des Status'); /* Failed to change status */
      }
    } catch (err) {
      console.error('Error changing status:', err);
      setError('Fehler beim Ändern des Status'); /* Failed to change status */
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = () => {
    setHistoryDialogOpen(true);
    fetchStatusHistory();
  };

  const formatDateLocal = (dateString: string) => {
    return formatDateTime(new Date(dateString));
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'draft': '#FF9800',
      'ready': '#4CAF50',
      'sent': '#2196F3',
      'partial': '#FF5722',
      'delivered': '#4CAF50',
      'completed': '#9C27B0'
    };
    return statusColors[status] || '#757575';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={!canEdit || availableTransitions.length === 0}
        >
          Status ändern {/* Change Status */}
        </Button>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={handleViewHistory}
        >
          Historie anzeigen {/* View History */}
        </Button>
      </Box>

      {/* Status Progress */}
      <StatusProgress status={order.status} />

      {/* Status Change Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bestellstatus ändern</DialogTitle> {/* Change Order Status */}
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Neuer Status</InputLabel> {/* New Status */}
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Neuer Status"
            >
              {availableTransitions.map((transition) => (
                <MenuItem key={transition.status} value={transition.status}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={transition.metadata.label}
                      size="small"
                      sx={{
                        backgroundColor: transition.metadata.color,
                        color: 'white'
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {transition.metadata.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Grund für Änderung" /* Reason for Change */
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="z.B. Kundengenehmigung erhalten" /* e.g., Customer approval received */
          />

          <TextField
            fullWidth
            label="Zusätzliche Anmerkungen" /* Additional Notes */
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Optionale zusätzliche Details..." /* Optional additional details... */
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button> {/* Cancel */}
          <Button
            onClick={handleStatusChange}
            variant="contained"
            disabled={!selectedStatus || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Status ändern'} {/* Change Status */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Status-Änderungsverlauf</DialogTitle> {/* Status Change History */}
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : statusHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              Noch keine Statusänderungen aufgezeichnet {/* No status changes recorded yet */}
            </Typography>
          ) : (
            <List>
              {statusHistory.map((log, index) => (
                <React.Fragment key={log.id}>
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={log.from_status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(log.from_status),
                            color: 'white'
                          }}
                        />
                        <Typography variant="body2">→</Typography>
                        <Chip
                          label={log.to_status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(log.to_status),
                            color: 'white'
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Geändert von: {log.changed_by} {/* Changed by: */}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {formatDateLocal(log.created_at)}
                      </Typography>
                    </Box>

                    {log.change_reason && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <EditIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          <strong>Grund:</strong> {log.change_reason} {/* Reason: */}
                        </Typography>
                      </Box>
                    )}

                    {log.change_notes && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <NotesIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          <strong>Anmerkungen:</strong> {log.change_notes} {/* Notes: */}
                        </Typography>
                      </Box>
                    )}
                  </ListItem>
                  {index < statusHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Schließen</Button> {/* Close */}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
